import { customElement, FASTElement, observable } from '@microsoft/fast-element';
import { RfqFormTemplate as template } from './rfq-form.template';
import { RfqFormStyles as styles } from './rfq-form.styles';
import { CASCADE_ENUM, EnumTranslate, IEnumService } from '../../../../services/enum.service';
import { SocketSubscription } from '@genesislcap/foundation-comms';
import { ICommonService, IRfqService } from '../../../../services';
import { IHubZoneMatrix, IRfq, IRfqInsert } from '../../../../types';
import { NumberField, TextField } from '@microsoft/fast-foundation';
import { Select } from '@genesislcap/foundation-ui';
import {
  clearErrorUtil,
  CONTRACT_TYPE,
  displayErrorUtil,
  PROFILE_RIGHTS,
  PRODUCT_TYPE,
  updateGuaranteedCOD,
  VPPA_RFQ_STATUS,
  RFQ_APPLICABLE_REC_PROGRAM,
  CONTRACT_CREDIT_PROFILE,
} from '../../../../utils';
import {
  formatDateForInput,
  withoutTime,
  convertDateToUTC,
  roundNumber,
  roundNumberOptionalField,
} from '../../../../utils/formatting';
import { ICompanyService } from '../../../../services/company.service';
import { SegmentedControl } from '@genesislcap/foundation-zero';
import { IUserService } from '../../../../services/user.service';
const name = 'rfq-form';

@customElement({
  name,
  template,
  styles,
})
export class RfqForm extends FASTElement {
  @IUserService userService: IUserService;

  // Services used
  @IEnumService enumService: IEnumService;
  @ICommonService commonService: ICommonService;
  @IRfqService rfqService: IRfqService;
  @ICompanyService companyService: ICompanyService;

  // HTML objects reference
  @observable rfqStatusElement: Select;
  @observable rfqCreditProfileElement: Select;
  @observable productTypeElement: Select;
  @observable technologyTypeElement: SegmentedControl;
  @observable contractTypeElement: Select;
  @observable countryElement: Select;
  @observable stateElement: Select;
  @observable cityElement: Select;
  @observable targetCodElement: TextField;
  @observable guaranteedCodElement: TextField;
  @observable requestedVolumeElement: TextField;
  @observable isoElement: Select;
  @observable settlementPointElement: Select;
  @observable settlementIndexElement: Select;
  @observable tenorStartDateElement: TextField;
  @observable tenorMonthElement: TextField;
  @observable negativePriceMethodElement: Select;
  @observable applicableTrackingSystemElement: Select;
  @observable recDeliveryMethodologyElement: Select;
  @observable performanceGuaranteeMethodElement: Select;
  @observable applicableRECProgramElement: Select;
  @observable fixedPriceUpperBoundElement: TextField;
  @observable fixedPriceLowerBoundElement: TextField;
  @observable rfqReferenceElement: TextField;
  @observable buyerPostCodCollateralElement: TextField;

  @observable expectedSettlementMaxElement: NumberField;
  @observable expectedSettlementMinElement: NumberField;
  @observable downsideRiskMaxElement: NumberField;
  @observable downsideRiskMinElement: NumberField;
  @observable nodalLmeHistoricMaxElement: NumberField;
  @observable nodalLmeHistoricMinElement: NumberField;
  @observable impliedCarbonPriceMaxElement: NumberField;
  @observable impliedCarbonPriceMinElement: NumberField;
  @observable carbonFreeEnergyUpperBoundElement: NumberField;
  @observable carbonFreeEnergyLowerBoundElement: NumberField;
  @observable fixedPriceElement: NumberField;
  @observable capacityElement: NumberField;
  @observable floorQuantityElement: NumberField;

  // ENUMs list
  @observable rfqStatuses: Array<EnumTranslate>;
  @observable contractTypes: Array<EnumTranslate> = [];
  @observable negativePricingMethod: Array<EnumTranslate>;
  @observable applicableTrackingSystem: Array<EnumTranslate>;
  @observable recDeliveryMethodology: Array<EnumTranslate>;
  @observable performanceGuaranteeMethod: Array<EnumTranslate>;
  @observable applicableRECProgram: Array<EnumTranslate>;
  @observable creditProfile: Array<EnumTranslate>;
  @observable productTypes: Array<EnumTranslate>;

  // Hub Zone matrix List related
  private _hubZoneMatrix: Array<IHubZoneMatrix> = [];
  private _filteredHubZoneMatrix: Array<IHubZoneMatrix> = [];
  @observable _filteredCreditProfile: Array<EnumTranslate> = [];
  @observable _iso = [];
  @observable _settlementPoint: Array<string> = [];
  @observable _settlementIndex: Array<string> = [];
  @observable _allCountries = [];
  @observable _allStates = [];
  @observable _cities = [];

  // Values inserted on fields
  @observable rfqStatusValue: string;
  @observable technologyTypeValue: string;
  @observable contractTypeValue: string;
  @observable countryValue: string;
  @observable stateValue: string;
  @observable cityValue: string;
  @observable targetCodValue: any;
  @observable guaranteedCodValue: any;
  @observable requestedVolumeValue: string = '';
  @observable displayCod: boolean = true;
  @observable isoValue: string;
  @observable settlementPointValue: string;
  @observable settlementIndexValue: string;
  @observable tenorStartDateValue: any;
  @observable tenorMonthValue: string = '';
  @observable negativePriceMethodValue: string;
  @observable applicableTrackingSystemValue: string;
  @observable recDeliveryMethodologyValue: string;
  @observable performanceGuaranteeMethodValue: string;
  @observable applicableRECProgramValue: string;
  @observable fixedPriceUpperBoundValue: string = '';
  @observable fixedPriceLowerBoundValue: string = '';
  @observable rfqReferenceValue: string;
  @observable expectedSettlementMaxValue: string = '';
  @observable expectedSettlementMinValue: string = '';
  @observable downsideRiskMaxValue: string = '';
  @observable downsideRiskMinValue: string = '';
  @observable nodalLmeHistoricMaxValue: string = '';
  @observable nodalLmeHistoricMinValue: string = '';
  @observable impliedCarbonPriceMaxValue: string = '';
  @observable impliedCarbonPriceMinValue: string = '';
  @observable carbonFreeEnergyUpperBoundValue: string = '';
  @observable carbonFreeEnergyLowerBoundValue: string = '';
  @observable creditProfileValue: string = '';
  @observable buyerPostCodCollateralValue: string = '';

  public defaultPostCodCollateralValue = 100000;
  @observable fixedPriceValue: string = '';
  @observable capacityValue: string = '';
  @observable productTypeValue: string;
  @observable floorQuantityValue: string = '';

  // Control
  @observable isEditModal: boolean = false;
  @observable rfqId: string;
  private _requiredFormFields = [];
  private projectSubscription: SocketSubscription;
  private existingRfq: IRfq;
  @observable newBuildContracts: String[] = [
    CONTRACT_TYPE.NEW_BUILD_VPPA,
    CONTRACT_TYPE.NEW_BUILD_PPA,
  ];
  @observable isCreditWorthy: boolean = false;
  @observable isBusinessHours: boolean;
  // Only false if trying to auto execute outside of business hours
  @observable isAbleToExecute: boolean = true;
  @observable isInvestmentGrade: boolean;
  @observable autoExecString: string = CONTRACT_TYPE.MINI_POST_BUILD_VPPA_AUTO_EXECUTED;
  @observable recString: string = CONTRACT_TYPE.REC;
  private isTenorDateValid: boolean = false;
  private isTenorMonthsValid: boolean = false;
  private isCapacityValid: boolean = false;
  private isContractTypeValid: boolean = false;
  private isFixedPriceBoundsValid: boolean = false;
  @observable technologyTypeBoth: string = 'BOTH';
  @observable technologyTypeWind: string = 'WIND';
  @observable technologyTypeSolar: string = 'SOLAR';

  private rangeFields: Array<NumberField> = [];


  async connectedCallback() {
    super.connectedCallback();
    this.createEventListeners();
    setTimeout(() => {
      this.technologyTypeValue = this.technologyTypeWind;
    }, 100);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.projectSubscription?.unsubscribe();
  }

  public async loadData(rfqId: string, isEditModal: boolean) {
    this.rfqId = rfqId;
    this.isEditModal = isEditModal;
    await this.getCountries();
    await this.getStates();
    await this.loadEnums();
    await this.loadBusinessHourFlag();
    await this.loadHubZoneMatrix();
    await this.loadInvestmentGrade();
    await this.filterCreditProfile();
    this.loadRequiredFields();
    if (this.rfqId) {
      await this.loadRfqData();
    }
  }

  async loadBusinessHourFlag() {
    await this.commonService.isItIsBuyingHours().then((response) => {
      if (response.REPLY) {
        this.isBusinessHours = response.REPLY[0].IS_IT_IS_BUYING_HOURS;
      }
    });
  }

  async loadRfqData() {
    const response = await this.rfqService.getRfq(this.rfqId);
    if (response?.REPLY) {
      this.existingRfq = response.REPLY[0];
      // If it's an edit modal, use the existing RFQ status, if it's a copy modal, use the active status
      this.rfqStatusValue = this.isEditModal ? this.existingRfq.RFQ_STATUS : VPPA_RFQ_STATUS.ACTIVE;
      this.technologyTypeValue = this.existingRfq.TECHNOLOGY;
      this.contractTypeValue = this.existingRfq.CONTRACT_TYPE;
      // value only updated if the contract type is auto executed
      if (this.contractTypeValue === this.autoExecString) {
        this.isAbleToExecute = this.isBusinessHours;
      }
      this.productTypeValue = this.existingRfq.PRODUCT_TYPE;
      this.countryValue = this.existingRfq.COUNTRY_NAME;
      this.stateValue = this.existingRfq.STATE_NAME;
      if (this.stateValue) {
        await this.getCities();
        this.setCity();
      }
      if (this.existingRfq.ISO) {
        setTimeout(() => {
          this.isoValue = this.existingRfq.ISO;
          this.isoSelected();
          setTimeout(() => {
            // Race Condition: Need to wait a little before checking the completion status
            // or edit/copy buttons for RECs won't be enabled initially
            this.checkFormCompletionStatus();
          }, 100);
          if (this.existingRfq.SETTLEMENT_POINT) {
            setTimeout(() => {
              this.settlementPointValue = this.existingRfq.SETTLEMENT_POINT;
              this.checkFormCompletionStatus();
              this.settlementPointSelected();
              setTimeout(() => {
                this.settlementIndexValue = this.existingRfq.SETTLEMENT_INDEX_STRING;
                setTimeout(() => {
                  this.checkFormCompletionStatus();
                }, 100);
              }, 100);
            }, 100);
          }
        }, 100);
      }
      this.targetCodValue = formatDateForInput(this.existingRfq.TARGET_COD as number);
      this.guaranteedCodValue = formatDateForInput(this.existingRfq.GUARANTEED_COD as number);
      this.requestedVolumeValue = this.existingRfq.REQUESTED_VOLUME
        ? this.existingRfq.REQUESTED_VOLUME.toString()
        : '';
      if (this.existingRfq.FIXED_PRICE_LOWER_BOUND || this.existingRfq.FIXED_PRICE_UPPER_BOUND) {
        this.fixedPriceUpperBoundValue = this.existingRfq.FIXED_PRICE_UPPER_BOUND
          ? this.existingRfq.FIXED_PRICE_UPPER_BOUND.toString()
          : '';
        this.fixedPriceLowerBoundValue = this.existingRfq.FIXED_PRICE_LOWER_BOUND
          ? this.existingRfq.FIXED_PRICE_LOWER_BOUND.toString()
          : '';
        this.validateFixedPriceBounds();
      }

      if (this.existingRfq.TENOR_START_DATE) {
        this.tenorStartDateValue = formatDateForInput(this.existingRfq.TENOR_START_DATE as number);
        this.checkTenorValidity();
      }

      if (this.existingRfq.TENOR_MONTHS) {
        this.tenorMonthValue = this.existingRfq.TENOR_MONTHS
          ? this.existingRfq.TENOR_MONTHS.toString()
          : '';
        this.checkTenorMonthsValidity();
      }

      if (this.existingRfq.CAPACITY) {
        this.capacityValue = this.existingRfq.CAPACITY ? this.existingRfq.CAPACITY.toString() : '';
        this.checkOfferedCapacityValidity();
      }
      if (this.contractTypeValue) {
        this.isContractTypeValid = true;
      }

      this.negativePriceMethodValue = this.existingRfq.RFQ_NEGATIVE_PRICING_METHOD;
      this.applicableTrackingSystemValue = this.existingRfq.APPLICABLE_TRACKING_SYSTEM;
      this.recDeliveryMethodologyValue = this.existingRfq.RFQ_REC_DELIVERY_METHODOLOGY;
      this.performanceGuaranteeMethodValue = this.existingRfq.RFQ_PERFORMANCE_GUARANTEE_METHOD;
      this.applicableRECProgramValue = this.existingRfq.RFQ_APPLICABLE_REC_PROGRAM;
      this.expectedSettlementMaxValue = this.existingRfq.EXPECTED_SETTLEMENT_MAX
        ? this.existingRfq.EXPECTED_SETTLEMENT_MAX.toString()
        : '';
      this.expectedSettlementMinValue = this.existingRfq.EXPECTED_SETTLEMENT_MIN
        ? this.existingRfq.EXPECTED_SETTLEMENT_MIN.toString()
        : '';
      this.downsideRiskMaxValue = this.existingRfq.DOWNSIDE_RISK_MAX
        ? this.existingRfq.DOWNSIDE_RISK_MAX.toString()
        : '';
      this.downsideRiskMinValue = this.existingRfq.DOWNSIDE_RISK_MIN
        ? this.existingRfq.DOWNSIDE_RISK_MIN.toString()
        : '';
      this.nodalLmeHistoricMaxValue = this.existingRfq.NODAL_LME_HISTORIC_MAX
        ? this.existingRfq.NODAL_LME_HISTORIC_MAX.toString()
        : '';
      this.nodalLmeHistoricMinValue = this.existingRfq.NODAL_LME_HISTORIC_MIN
        ? this.existingRfq.NODAL_LME_HISTORIC_MIN.toString()
        : '';
      this.impliedCarbonPriceMaxValue = this.existingRfq.IMPLIED_CARBON_PRICE_MAX
        ? this.existingRfq.IMPLIED_CARBON_PRICE_MAX.toString()
        : '';
      this.impliedCarbonPriceMinValue = this.existingRfq.IMPLIED_CARBON_PRICE_MIN
        ? this.existingRfq.IMPLIED_CARBON_PRICE_MIN.toString()
        : '';
      this.carbonFreeEnergyUpperBoundValue = this.existingRfq.CARBON_FREE_ENERGY_UPPER_BOUND
        ? this.existingRfq.CARBON_FREE_ENERGY_UPPER_BOUND.toString()
        : '';
      this.carbonFreeEnergyLowerBoundValue = this.existingRfq.CARBON_FREE_ENERGY_LOWER_BOUND
        ? this.existingRfq.CARBON_FREE_ENERGY_LOWER_BOUND.toString()
        : '';
      this.rfqReferenceValue = this.existingRfq.RFQ_REFERENCE;
      this.creditProfileValue = this.existingRfq.RFQ_CREDIT_PROFILE
        ? this.existingRfq.RFQ_CREDIT_PROFILE
        : '';
      this.buyerPostCodCollateralValue = this.existingRfq.BUYER_POST_COD_COLLATERAL
        ? this.existingRfq.BUYER_POST_COD_COLLATERAL.toString()
        : '';
      this.fixedPriceValue = this.existingRfq.FIXED_PRICE
        ? this.existingRfq.FIXED_PRICE.toString()
        : '';
      this.floorQuantityValue = this.existingRfq.FLOOR_QUANTITY
        ? this.existingRfq.FLOOR_QUANTITY.toString()
        : '';
      this.loadRequiredFields();
    }
  }

  private async loadInvestmentGrade() {
    this.isInvestmentGrade = await this.companyService.isInvestmentGrade();
  }

  private generateSettlementPointDD() {
    // Get all rows with selected ISO
    const selectedPoints = this._hubZoneMatrix.filter((item) => item.ISO === this.isoValue);

    // Get Unique Settlement Points for DD
    this._settlementPoint = [...new Set(selectedPoints.map((item) => item.SETTLEMENT_POINT))];
  }

  private generateISODD() {
    this._iso = [...new Set(this._hubZoneMatrix.map((item) => item.ISO))];
  }

  public isoSelected() {
    // Reset previously selected values
    this.settlementIndexValue = '';
    this.settlementPointValue = '';
    this._settlementIndex = [];
    this._settlementPoint = [];

    // Generate dropdown values
    if (this.isoValue) {
      this.generateSettlementPointDD();
    }
  }

  public applicableRecProgramSelected() {
    if (!this.isApplicableRecProgramSelected()) {
      this.applicableTrackingSystemValue = '';
      this.recDeliveryMethodologyValue = '';
      clearErrorUtil(this.applicableTrackingSystemElement);
      clearErrorUtil(this.recDeliveryMethodologyElement);
    }
    this.loadRequiredFields();
  }

  public isApplicableRecProgramSelected(): boolean {
    return (
      this.applicableRECProgramValue &&
      this.applicableRECProgramValue !== RFQ_APPLICABLE_REC_PROGRAM.NONE
    );
  }

  public settlementPointSelected() {
    this._settlementIndex = [];
    this.settlementIndexValue = '';
    if (this.settlementPointValue) {
      this._filteredHubZoneMatrix = this._hubZoneMatrix.filter(
        (item) => item.SETTLEMENT_POINT === this.settlementPointValue,
      );
      this._settlementIndex = [
        ...new Set(this._filteredHubZoneMatrix.map((item) => item.SETTLEMENT_INDEX)),
      ];
    }
  }

  public settlementIndexSelected() {
    const selectedHubZoneMatrix = this._filteredHubZoneMatrix.find(
      (item) => item.SETTLEMENT_INDEX === this.settlementIndexValue,
    );
  }

  public clearForm() {
    this.cleanClassValues();

    this.projectSubscription?.unsubscribe();

    this._hubZoneMatrix = [];
    this._filteredHubZoneMatrix = [];
    this._settlementPoint = [];
    this._settlementIndex = [];
    this.existingRfq = {} as IRfq;
    this.isEditModal = false;

    this.clearFieldValidity();
  }
  public reloadRfqData() {
    if (this.isEditModal) {
      this.cleanClassValues();
      this.reloadUneditableFields();
    }
  }
  public async reloadUneditableFields() {
    if (this.isEditModal) {
      const response = await this.rfqService.getRfq(this.rfqId);
      if (response?.REPLY) {
        this.existingRfq = response.REPLY[0];
        this.contractTypeValue = this.existingRfq.CONTRACT_TYPE;

        if (this.contractTypeValue) {
          this.isContractTypeValid = true;
        }
        this.loadRequiredFields();
      }
      this.checkFormCompletionStatus();
    }
  }

  public isFieldRequired(requiredStatus: boolean): string {
    return requiredStatus ? 'required' : '';
  }

  public cleanClassValues(keepAutoExecutedFieldsValues?: boolean) {
    if (keepAutoExecutedFieldsValues !== true) {
      this.productTypeValue = null;
      this.technologyTypeValue = 'WIND';
      this.contractTypeValue = null;
      this.isoValue = null;
      this.settlementPointValue = null;
      this.settlementIndexValue = null;
      this.tenorStartDateValue = null;
      this.tenorMonthValue = '';
    }
    this.rfqStatusValue = VPPA_RFQ_STATUS.ACTIVE;
    this.countryValue = null;
    this.stateValue = null;
    this.cityValue = null;
    this.targetCodValue = null;
    this.guaranteedCodValue = null;
    this.requestedVolumeValue = null;
    this.negativePriceMethodValue = null;
    this.applicableTrackingSystemValue = null;
    this.recDeliveryMethodologyValue = null;
    this.performanceGuaranteeMethodValue = null;
    this.applicableRECProgramValue = null;
    this.fixedPriceLowerBoundValue = '';
    this.fixedPriceUpperBoundValue = '';
    this.rfqReferenceValue = null;
    this.expectedSettlementMaxValue = null;
    this.expectedSettlementMinValue = null;
    this.downsideRiskMaxValue = null;
    this.downsideRiskMinValue = null;
    this.nodalLmeHistoricMaxValue = null;
    this.nodalLmeHistoricMinValue = null;
    this.impliedCarbonPriceMaxValue = null;
    this.impliedCarbonPriceMinValue = null;
    this.carbonFreeEnergyUpperBoundValue = null;
    this.carbonFreeEnergyLowerBoundValue = null;
    this.creditProfile = null;
    this.creditProfileValue = null;
    this.buyerPostCodCollateralValue = '';
    this.fixedPriceValue = null;
    this.capacityValue = null;
    this.floorQuantityValue = null;
  }

  private async loadEnums() {
    await this.enumService.getFilteredEnumValues(CASCADE_ENUM.PRODUCT_TYPE).then((response) => {
      if (response.REPLY) {
        this.productTypes = response.REPLY;
      }
    });

    await this.enumService.getFilteredEnumValues(CASCADE_ENUM.CONTRACT_TYPE).then((response) => {
      if (response.REPLY) {
        this.contractTypes = response.REPLY;
      }
    });

    await this.enumService
      .getEnumValues(CASCADE_ENUM.RFQ_NEGATIVE_PRICING_METHOD)
      .then((response) => {
        if (response.REPLY) {
          this.negativePricingMethod = response.REPLY;
        }
      });

    await this.enumService
      .getEnumValues(CASCADE_ENUM.APPLICABLE_TRACKING_SYSTEM)
      .then((response) => {
        if (response.REPLY) {
          this.applicableTrackingSystem = response.REPLY;
        }
      });

    await this.enumService
      .getEnumValues(CASCADE_ENUM.RFQ_REC_DELIVERY_METHODOLOGY)
      .then((response) => {
        if (response.REPLY) {
          this.recDeliveryMethodology = response.REPLY;
        }
      });

    await this.enumService
      .getEnumValues(CASCADE_ENUM.RFQ_PERFORMANCE_GUARANTEE_METHOD)
      .then((response) => {
        if (response.REPLY) {
          this.performanceGuaranteeMethod = response.REPLY;
        }
      });

    await this.enumService
      .getEnumValues(CASCADE_ENUM.RFQ_APPLICABLE_REC_PROGRAM)
      .then((response) => {
        if (response.REPLY) {
          this.applicableRECProgram = response.REPLY;
        }
      });

    await this.enumService.getEnumValues(CASCADE_ENUM.RFQ_CREDIT_PROFILE).then((response) => {
      if (response.REPLY) {
        this.creditProfile = response.REPLY;
      }
    });

    await this.enumService.getEnumValues(CASCADE_ENUM.RFQ_STATUS).then((response) => {
      if (response.REPLY) {
        this.rfqStatuses = response.REPLY;
      }
    });
  }
  private async loadHubZoneMatrix() {
    await this.commonService.getHubZoneMatrix().then((response) => {
      if (response.REPLY) {
        this._hubZoneMatrix = response.REPLY;
        this.generateISODD();
      }
    });
  }

  private async filterCreditProfile() {
    let filterProfiles: String[];
    if (this.isInvestmentGrade) {
      filterProfiles = [CONTRACT_CREDIT_PROFILE.PARENTAL_GUARANTEE_FROM_NON_INVESTMENT_ENTITY];
    } else {
      filterProfiles = [
        CONTRACT_CREDIT_PROFILE.PARENTAL_GUARANTEE_FROM_INVESTMENT_ENTITY,
        CONTRACT_CREDIT_PROFILE.INVESTMENT_GRADE_ENTITY,
      ];
    }
    this._filteredCreditProfile = this.creditProfile.filter(
      (x) => !filterProfiles.includes(x.ENUM_VALUE),
    );
  }

  private loadRequiredFields() {
    this._requiredFormFields = [
      this.technologyTypeElement,
      this.contractTypeElement,
      this.productTypeElement,
      this.tenorStartDateElement,
      this.tenorMonthElement,
      this.isoElement,
    ];

    if (this.contractTypeValue === this.autoExecString) {
      this._requiredFormFields.push(
        this.capacityElement,
        this.fixedPriceElement,
        this.settlementIndexElement,
        this.settlementPointElement,
      );
    } else if (this.contractTypeValue === this.recString) {
      this._requiredFormFields.push(
        this.fixedPriceElement,
        this.requestedVolumeElement,
        this.applicableRECProgramElement,
      );
    } else {
      this._requiredFormFields.push(
        this.settlementIndexElement,
        this.settlementPointElement,
        this.requestedVolumeElement,
        this.rfqCreditProfileElement,
        this.fixedPriceLowerBoundElement,
        this.fixedPriceUpperBoundElement,
        this.applicableRECProgramElement,
      );
    }

    if (this.isApplicableRecProgramSelected()) {
      this._requiredFormFields.push(
        this.applicableTrackingSystemElement,
        this.recDeliveryMethodologyElement,
      );
    }

    this._requiredFormFields.forEach((field) => {
      if (field === this.tenorStartDateElement) {
        field.addEventListener('change', () => this.checkTenorValidity());
        return;
      }
      if (field === this.capacityElement) {
        field.addEventListener('blur', () => this.checkOfferedCapacityValidity());
        return;
      }
      if (field === this.fixedPriceElement) {
        field.addEventListener('blur', () => this.checkFormCompletionStatus());
      }
      if (field === this.settlementIndexElement) {
        field.addEventListener('change', () => this.checkFormCompletionStatus());
      }
      if (field === this.settlementPointElement) {
        field.addEventListener('change', () => this.checkFormCompletionStatus());
      }

      if (field === this.tenorMonthElement) {
        field.addEventListener('blur', () => this.checkTenorMonthsValidity());
        return;
      }

      if (field instanceof Select) {
        field.addEventListener('change', () => this.checkFieldValidity(field));
      }

      if (
        field === this.fixedPriceUpperBoundElement ||
        field === this.fixedPriceLowerBoundElement
      ) {
        field.addEventListener('blur', () => this.validateFixedPriceBounds());
        return;
      }
      if (field === this.productTypeElement) {
        field.addEventListener('change', () => this.checkProductTypeValidity());
      }

      if (field === this.isoElement) {
        field.addEventListener('change', () => this.checkFormCompletionStatus());
      }

      field.addEventListener('blur', () => this.checkFieldValidity(field));
    });
    this.checkFormCompletionStatus();
  }

  private checkFieldValidity(elm: TextField | Select | NumberField) {
    if (!elm.value) {
      displayErrorUtil(elm);
    } else {
      if (elm === this.guaranteedCodElement) {
        this.guaranteedCodValueChanged();
      } else if (elm === this.targetCodElement) {
        clearErrorUtil(this.guaranteedCodElement);
        clearErrorUtil(elm);
        this.guaranteedCodValue = updateGuaranteedCOD(this.targetCodValue);
      } else {
        clearErrorUtil(elm);
      }
    }
    this.checkFormCompletionStatus();
  }

  private clearFieldValidity() {
    clearErrorUtil(this.targetCodElement);
    clearErrorUtil(this.impliedCarbonPriceMinElement);
    clearErrorUtil(this.downsideRiskMinElement);
    clearErrorUtil(this.expectedSettlementMinElement);
    clearErrorUtil(this.nodalLmeHistoricMinElement);
    this._requiredFormFields.forEach((field) => {
      clearErrorUtil(field);
    });
  }

  private checkFormCompletionStatus() {
    let isFormValid: Boolean;
    const isRangeFieldsValid = this.validateRangeFields();

    if (this.contractTypeValue === this.autoExecString) {
      isFormValid =
        this.isTenorDateValid &&
        this.isTenorMonthsValid &&
        this.isCapacityValid &&
        this.isContractTypeValid &&
        this._requiredFormFields.every((elm) => elm.value) &&
        isRangeFieldsValid;
    } else if (this.contractTypeValue === this.recString) {
      isFormValid =
        this.isContractTypeValid &&
        this._requiredFormFields.every((elm) => elm.value) &&
        isRangeFieldsValid;
    } else {
      isFormValid =
        this.isFixedPriceBoundsValid &&
        this.isContractTypeValid &&
        this._requiredFormFields.every((elm) => elm.value) &&
        isRangeFieldsValid;
    }

    this.$emit('formChanged', isFormValid);
  }

  public getFormData(): IRfqInsert {
    this.checkAndSetNARfqEnums();
    const formData: IRfqInsert = {
      RFQ_STATUS: this.rfqStatusValue,
      TECHNOLOGY: this.technologyTypeValue,
      CONTRACT_TYPE: this.contractTypeValue,
      COUNTRY_NAME: this.countryValue ? this.countryValue : null,
      STATE_NAME: this.stateValue ? this.stateValue : null,
      CITY_NAME: this.cityValue ? this.cityValue : null,
      TARGET_COD: this.targetCodValue,
      GUARANTEED_COD: this.guaranteedCodValue,
      REQUESTED_VOLUME: Number(this.requestedVolumeValue),
      FIXED_PRICE_LOWER_BOUND: Number(this.fixedPriceLowerBoundValue),
      FIXED_PRICE_UPPER_BOUND: Number(this.fixedPriceUpperBoundValue),
      ISO: this.isoValue,
      SETTLEMENT_POINT: this.settlementPointValue ? this.settlementPointValue : null,
      SETTLEMENT_INDEX_STRING: this.settlementIndexValue ? this.settlementIndexValue : null,
      TENOR_START_DATE: this.tenorStartDateValue,
      TENOR_MONTHS: Number(this.tenorMonthValue),
      RFQ_NEGATIVE_PRICING_METHOD: this.negativePriceMethodValue,
      APPLICABLE_TRACKING_SYSTEM: this.applicableTrackingSystemValue,
      RFQ_REC_DELIVERY_METHODOLOGY: this.recDeliveryMethodologyValue,
      RFQ_PERFORMANCE_GUARANTEE_METHOD: this.performanceGuaranteeMethodValue,
      RFQ_APPLICABLE_REC_PROGRAM: this.applicableRECProgramValue,
      CASCADE_COMPANY_ID: '0',
      RFQ_REFERENCE: this.rfqReferenceValue,
      EXPECTED_SETTLEMENT_MAX: Number(this.expectedSettlementMaxValue),
      EXPECTED_SETTLEMENT_MIN: Number(this.expectedSettlementMinValue),
      DOWNSIDE_RISK_MAX: Number(this.downsideRiskMaxValue),
      DOWNSIDE_RISK_MIN: Number(this.downsideRiskMinValue),
      NODAL_LME_HISTORIC_MAX: Number(this.nodalLmeHistoricMaxValue),
      NODAL_LME_HISTORIC_MIN: Number(this.nodalLmeHistoricMinValue),
      IMPLIED_CARBON_PRICE_MAX: Number(this.impliedCarbonPriceMaxValue),
      IMPLIED_CARBON_PRICE_MIN: Number(this.impliedCarbonPriceMinValue),
      CARBON_FREE_ENERGY_UPPER_BOUND: Number(this.carbonFreeEnergyUpperBoundValue),
      CARBON_FREE_ENERGY_LOWER_BOUND: Number(this.carbonFreeEnergyLowerBoundValue),
      BUYER_POST_COD_COLLATERAL: Number(this.buyerPostCodCollateralValue),
      RFQ_CREDIT_PROFILE: this.creditProfileValue ? this.creditProfileValue : 'OTHER',
      FIXED_PRICE: Number(this.fixedPriceValue),
      CAPACITY: Number(this.capacityValue),
      PRODUCT_TYPE: this.productTypeValue,
      FLOOR_QUANTITY: Number(this.floorQuantityValue),
    };

    return formData;
  }

  public getFullFormData(): IRfq {
    this.checkAndSetNARfqEnums();
    (this.existingRfq.RFQ_STATUS = this.rfqStatusValue),
      (this.existingRfq.TECHNOLOGY = this.technologyTypeValue),
      (this.existingRfq.CONTRACT_TYPE = this.contractTypeValue),
      (this.existingRfq.COUNTRY_NAME = this.countryValue ? this.countryValue : null),
      (this.existingRfq.STATE_NAME = this.stateValue ? this.stateValue : null),
      (this.existingRfq.CITY_NAME = this.cityValue ? this.cityValue : null),
      (this.existingRfq.TARGET_COD = this.targetCodValue),
      (this.existingRfq.GUARANTEED_COD = this.guaranteedCodValue),
      (this.existingRfq.REQUESTED_VOLUME = Number(this.requestedVolumeValue)),
      (this.existingRfq.ISO = this.isoValue),
      (this.existingRfq.SETTLEMENT_POINT = this.settlementPointValue
        ? this.settlementPointValue
        : null),
      (this.existingRfq.SETTLEMENT_INDEX_STRING = this.settlementIndexValue
        ? this.settlementIndexValue
        : null),
      (this.existingRfq.TENOR_START_DATE = this.tenorStartDateValue),
      (this.existingRfq.TENOR_MONTHS = Number(this.tenorMonthValue)),
      (this.existingRfq.RFQ_NEGATIVE_PRICING_METHOD = this.negativePriceMethodValue),
      (this.existingRfq.APPLICABLE_TRACKING_SYSTEM = this.applicableTrackingSystemValue),
      (this.existingRfq.RFQ_REC_DELIVERY_METHODOLOGY = this.recDeliveryMethodologyValue),
      (this.existingRfq.RFQ_PERFORMANCE_GUARANTEE_METHOD = this.performanceGuaranteeMethodValue),
      (this.existingRfq.RFQ_APPLICABLE_REC_PROGRAM = this.applicableRECProgramValue),
      (this.existingRfq.FIXED_PRICE_LOWER_BOUND = Number(this.fixedPriceLowerBoundValue)),
      (this.existingRfq.EXPECTED_SETTLEMENT_MAX = Number(this.expectedSettlementMaxValue));
    this.existingRfq.EXPECTED_SETTLEMENT_MIN = Number(this.expectedSettlementMinValue);
    this.existingRfq.DOWNSIDE_RISK_MAX = Number(this.downsideRiskMaxValue);
    this.existingRfq.DOWNSIDE_RISK_MIN = Number(this.downsideRiskMinValue);
    this.existingRfq.NODAL_LME_HISTORIC_MAX = Number(this.nodalLmeHistoricMaxValue);
    this.existingRfq.NODAL_LME_HISTORIC_MIN = Number(this.nodalLmeHistoricMinValue);
    this.existingRfq.IMPLIED_CARBON_PRICE_MAX = Number(this.impliedCarbonPriceMaxValue);
    this.existingRfq.IMPLIED_CARBON_PRICE_MIN = Number(this.impliedCarbonPriceMinValue);
    this.existingRfq.CARBON_FREE_ENERGY_UPPER_BOUND = Number(this.carbonFreeEnergyUpperBoundValue);
    this.existingRfq.CARBON_FREE_ENERGY_LOWER_BOUND = Number(this.carbonFreeEnergyLowerBoundValue);
    this.existingRfq.RFQ_REFERENCE = this.rfqReferenceValue;
    this.existingRfq.FIXED_PRICE_UPPER_BOUND = Number(this.fixedPriceUpperBoundValue);
    this.existingRfq.BUYER_POST_COD_COLLATERAL = Number(this.buyerPostCodCollateralValue);
    this.existingRfq.FIXED_PRICE = Number(this.fixedPriceValue);
    this.existingRfq.CAPACITY = Number(this.capacityValue);
    this.existingRfq.PRODUCT_TYPE = this.productTypeValue;
    this.existingRfq.RFQ_CREDIT_PROFILE = this.creditProfileValue ? this.creditProfileValue : null;
    this.existingRfq.FLOOR_QUANTITY = Number(this.floorQuantityValue);

    return this.existingRfq;
  }

  public stateSelected() {
    this.cityValue = '';
    if (this.stateValue) {
      this.getCities();
    }
  }

  private async getCountries() {
    await this.commonService.getAllCountries().then((response) => {
      if (response.REPLY) {
        this._allCountries = response.REPLY.sort((a, b) =>
          a.COUNTRY_NAME.localeCompare(b.COUNTRY_NAME),
        );
        setTimeout(() => {
          this.countryValue = 'United States';
          this.checkFormCompletionStatus();
        }, 100);
      }
    });
  }

  private async getStates() {
    await this.commonService.getAllStates().then((response) => {
      if (response.REPLY) {
        this._allStates = response.REPLY.sort((a, b) => a.STATE_NAME.localeCompare(b.STATE_NAME));
      }
    });
  }

  private setCity() {
    if (this.existingRfq && this.existingRfq.CITY_NAME) {
      setTimeout(() => {
        this.cityValue = this.existingRfq.CITY_NAME;
        this.checkFormCompletionStatus();
      }, 100);
    }
  }

  private async getCities() {
    const stateName = this.stateValue;
    await this.commonService.getCities(stateName).then((response) => {
      if (response.REPLY) {
        this._cities = response.REPLY.sort((a, b) => a.CITY_NAME.localeCompare(b.CITY_NAME));
      }
    });
  }

  public validateFixedPriceBounds() {
    this.isFixedPriceBoundsValid = true;
    clearErrorUtil(this.fixedPriceLowerBoundElement);
    if (this.fixedPriceLowerBoundValue && this.fixedPriceUpperBoundValue) {
      if (Number(this.fixedPriceLowerBoundValue) > Number(this.fixedPriceUpperBoundValue)) {
        this.isFixedPriceBoundsValid = false;
        displayErrorUtil(
          this.fixedPriceLowerBoundElement,
          'Price From must be less than or equal to Price To',
        );
      } else {
        this.isFixedPriceBoundsValid = true;
        clearErrorUtil(this.fixedPriceLowerBoundElement);
      }
    } else {
      this.isFixedPriceBoundsValid = false;
      displayErrorUtil(this.fixedPriceLowerBoundElement, 'Price range is required');
    }
    this.checkFormCompletionStatus();
  }

  public async validateContractType() {
    if (this.capacityValue) {
      this.checkOfferedCapacityValidity();
    }
    if (this.tenorStartDateValue) {
      this.checkTenorValidity();
    }
    if (this.tenorMonthValue) {
      this.checkTenorMonthsValidity();
    }
    if (this.fixedPriceValue) {
      this.checkFormCompletionStatus();
    }
    if (this.settlementIndexValue) {
      this.checkFormCompletionStatus();
    }
    if (this.settlementPointValue) {
      this.checkFormCompletionStatus();
    }
    if (this.contractTypeValue) {
      this.isContractTypeValid = true;
      clearErrorUtil(this.contractTypeElement);
      if (this.contractTypeValue === this.autoExecString) {
        this.isAbleToExecute = this.isBusinessHours;
      } else {
        this.isAbleToExecute = true;
      }
    } else if (this.productTypeValue && this.isContractTypeValid === false) {
      displayErrorUtil(this.contractTypeElement);
    }
    this.loadRequiredFields();
    this.checkFormCompletionStatus();
  }

  public technologyTypeChanged(technology: string) {
    this.technologyTypeValue = technology;
    if (this.contractTypeValue && this.technologyTypeValue) {
      if (
        this.contractTypeValue === this.autoExecString &&
        this.technologyTypeValue === this.technologyTypeBoth
      ) {
        this.contractTypeValue = null;
        clearErrorUtil(this.contractTypeElement);
      }
    }
  }

  public creditProfileSelected() {
    if (!this.creditProfileValue) {
      displayErrorUtil(this.rfqCreditProfileElement, 'Please select credit profile option');
      return;
    } else {
      clearErrorUtil(this.rfqCreditProfileElement);
    }
    this.isCreditWorthy = this.creditProfileValue == 'LETTER_OF_CREDIT';
    if (!this.isCreditWorthy) {
      this.buyerPostCodCollateralValue = '';
    } else {
      this.buyerPostCodCollateralValue = this.defaultPostCodCollateralValue.toString();
    }
  }

  public contractTypeValueChanged() {
    if (
      this.contractTypeValue != null &&
      (this.contractTypeValue === this.recString ||
        !this.newBuildContracts.includes(this.contractTypeValue))
    ) {
      this.targetCodValue = null;
      this.guaranteedCodValue = null;
      this.displayCod = false;
    } else {
      this.displayCod = true;
    }
    this.validateContractType();
  }

  public guaranteedCodValueChanged() {
    if (this.guaranteedCodValue) {
      if (this.targetCodValue) {
        const guaranteedCodDateTime = new Date(this.guaranteedCodValue).getTime();
        const targetCodDateTime = new Date(this.targetCodValue).getTime();
        if (guaranteedCodDateTime <= targetCodDateTime) {
          displayErrorUtil(
            this.guaranteedCodElement,
            'Guaranteed COD date must be greater than Target COD date',
          );
        } else {
          clearErrorUtil(this.guaranteedCodElement);
        }
      } else {
        displayErrorUtil(this.guaranteedCodElement, 'Please complete Target COD');
      }
    }
  }

  public targetCodValueChanged() {
    if (this.targetCodValue) {
      clearErrorUtil(this.guaranteedCodElement);
      this.guaranteedCodValue = updateGuaranteedCOD(this.targetCodValue);
    } else {
      clearErrorUtil(this.guaranteedCodElement);
    }
  }

  public validateInputForDateElements(e) {
    const date: string = e.target.value;
    const curYearInput = date.split('-', 1)[0];
    const curMonthInput = date.split('-')[1];
    const curDayInput = date.split('-')[2];
    if (curYearInput.length > 4) {
      this.targetCodValue = curYearInput.slice(0, -1) + '-' + curMonthInput + '-' + curDayInput;
    }
    if (!this.isEditModal) {
      if (withoutTime(new Date()) <= withoutTime(convertDateToUTC(new Date(this.targetCodValue)))) {
        clearErrorUtil(this.targetCodElement);
      } else {
        displayErrorUtil(this.targetCodElement, 'Target COD cannot be in the past');
      }
    }
  }

  private createEventListeners() {
    this.fixedPriceLowerBoundElement.addEventListener('blur', () =>
      this.validateFixedPriceBounds(),
    );

    this.fixedPriceUpperBoundElement.addEventListener('blur', () =>
      this.validateFixedPriceBounds(),
    );
    this.requestedVolumeElement.addEventListener('blur', () => this.checkFormCompletionStatus());

    this.buyerPostCodCollateralElement.addEventListener('blur', () =>
      this.checkFormCompletionStatus(),
    );

    // Ranged Value Selection Functions

    // Expected Settlement
    this.expectedSettlementMinElement.addEventListener('blur', () =>
      this.checkNumberRangeRules(
        this.expectedSettlementMinValue,
        this.expectedSettlementMaxValue,
        this.expectedSettlementMinElement,
      ),
    );

    this.expectedSettlementMaxElement.addEventListener('blur', () =>
      this.checkNumberRangeRules(
        this.expectedSettlementMinValue,
        this.expectedSettlementMaxValue,
        this.expectedSettlementMinElement,
      ),
    );

    // Downside Risk
    this.downsideRiskMinElement.addEventListener('blur', () =>
      this.checkNumberRangeRules(
        this.downsideRiskMinValue,
        this.downsideRiskMaxValue,
        this.downsideRiskMinElement,
      ),
    );

    this.downsideRiskMaxElement.addEventListener('blur', () =>
      this.checkNumberRangeRules(
        this.downsideRiskMinValue,
        this.downsideRiskMaxValue,
        this.downsideRiskMinElement,
      ),
    );

    // Historical Nodal LME
    this.nodalLmeHistoricMaxElement.addEventListener('blur', () => this.validateNodalLmeValues());
    this.nodalLmeHistoricMinElement.addEventListener('blur', () => this.validateNodalLmeValues());

    // Implied Carbon Price Range
    this.impliedCarbonPriceMinElement.addEventListener('blur', () =>
      this.checkNumberRangeRules(
        this.impliedCarbonPriceMinValue,
        this.impliedCarbonPriceMaxValue,
        this.impliedCarbonPriceMinElement,
      ),
    );

    this.impliedCarbonPriceMaxElement.addEventListener('blur', () =>
      this.checkNumberRangeRules(
        this.impliedCarbonPriceMinValue,
        this.impliedCarbonPriceMaxValue,
        this.impliedCarbonPriceMinElement,
      ),
    );

    // Carbon Free Energy
    this.carbonFreeEnergyLowerBoundElement.addEventListener('blur', () =>
      this.validateCarbonFreeEnergyValues(),
    );

    this.carbonFreeEnergyUpperBoundElement.addEventListener('blur', () =>
      this.validateCarbonFreeEnergyValues(),
    );

    this.rangeFields = [
      this.expectedSettlementMinElement,
      this.downsideRiskMinElement,
      this.nodalLmeHistoricMinElement,
      this.impliedCarbonPriceMinElement,
      this.carbonFreeEnergyLowerBoundElement,
    ];
  }

  private validateRangeFields(): boolean {
    return (
      this.rangeFields.filter((field) =>
        field.parentElement.parentElement.classList.contains('with-error'),
      ).length == 0
    );
  }

  public validateNodalLmeValues(): boolean {
    let isValid: boolean = true;

    if (
      this.nodalLmeHistoricMaxValue &&
      (Number(this.nodalLmeHistoricMaxValue) < 0 || Number(this.nodalLmeHistoricMaxValue) > 1)
    ) {
      displayErrorUtil(this.nodalLmeHistoricMinElement, `Values must be between 0 and 1`);
      isValid = false;
    } else if (
      this.nodalLmeHistoricMinValue &&
      (Number(this.nodalLmeHistoricMinValue) < 0 || Number(this.nodalLmeHistoricMinValue) > 1)
    ) {
      displayErrorUtil(this.nodalLmeHistoricMinElement, `Values must be between 0 and 1`);
      isValid = false;
    } else if (
      this.nodalLmeHistoricMinValue &&
      this.nodalLmeHistoricMaxValue &&
      this.nodalLmeHistoricMinValue > this.nodalLmeHistoricMaxValue
    ) {
      displayErrorUtil(
        this.nodalLmeHistoricMinElement,
        `Max value must be greater than or equal to Min value`,
      );
      isValid = false;
    } else {
      clearErrorUtil(this.nodalLmeHistoricMinElement);
    }

    this.checkFormCompletionStatus();

    return isValid;
  }

  public validateCarbonFreeEnergyValues(): boolean {
    let isValid: boolean = true;

    if (
      this.carbonFreeEnergyLowerBoundValue &&
      (Number(this.carbonFreeEnergyLowerBoundValue) < 0 ||
        Number(this.carbonFreeEnergyLowerBoundValue) > 100)
    ) {
      displayErrorUtil(
        this.carbonFreeEnergyLowerBoundElement,
        `Percentages must be between 0 and 100`,
      );
      isValid = false;
    } else if (
      this.carbonFreeEnergyUpperBoundValue &&
      (Number(this.carbonFreeEnergyUpperBoundValue) < 0 ||
        Number(this.carbonFreeEnergyUpperBoundValue) > 100)
    ) {
      displayErrorUtil(
        this.carbonFreeEnergyLowerBoundElement,
        `Percentages must be between 0 and 100`,
      );
      isValid = false;
    } else if (
      this.carbonFreeEnergyLowerBoundValue &&
      this.carbonFreeEnergyUpperBoundValue &&
      this.carbonFreeEnergyLowerBoundValue > this.carbonFreeEnergyUpperBoundValue
    ) {
      displayErrorUtil(
        this.carbonFreeEnergyLowerBoundElement,
        `Upper Bound must be greater than or equal to Lower Bound`,
      );
      isValid = false;
    } else {
      clearErrorUtil(this.carbonFreeEnergyLowerBoundElement);
    }

    this.checkFormCompletionStatus();

    return isValid;
  }

  private checkNumberRangeRules(minValue: string, maxValue: string, element: NumberField) {
    let isValid: boolean = true;

    if (minValue && maxValue && minValue > maxValue) {
      displayErrorUtil(element, `Max value must be greater than or equal to Min value`);
      isValid = false;
    } else {
      clearErrorUtil(element);
    }

    this.checkFormCompletionStatus();

    return isValid;
  }
  public validatePercentage(value: string, element: NumberField): string {
    const valueAux = Number(value);
    if ((valueAux && valueAux < 0) || valueAux > 100) {
      displayErrorUtil(element, 'Percentages must be between 0 and 100');
    } else {
      clearErrorUtil(element);
    }

    this.checkFormCompletionStatus();
    return roundNumberOptionalField(value);
  }

  public validateSwapPrices(
    lowerBoundValue: string,
    lowerBoundElement: NumberField,
    upperBoundValue: string,
    upperBoundElement: NumberField,
    upperBoundFieldName: string,
  ) {
    clearErrorUtil(lowerBoundElement);
    clearErrorUtil(upperBoundElement);
    if (lowerBoundValue && upperBoundValue) {
      if (Number(lowerBoundValue) > Number(upperBoundValue)) {
        displayErrorUtil(
          lowerBoundElement,
          upperBoundFieldName + ' must be greater than or equal to Floor Price',
        );
      } else {
        clearErrorUtil(lowerBoundElement);
      }
    }
    this.checkFormCompletionStatus();
  }
  public validateAndRoundPriceField(
    lowerBoundValue: string,
    lowerBoundElement: NumberField,
    upperBoundValue: string,
    upperBoundElement: NumberField,
    upperBoundFieldName: string,
    numberToBeRounded: string,
  ) {
    this.validateSwapPrices(
      lowerBoundValue,
      lowerBoundElement,
      upperBoundValue,
      upperBoundElement,
      upperBoundFieldName,
    );
    return roundNumberOptionalField(numberToBeRounded, 2);
  }
  public validateTenorStartDateValue() {
    const startDate = new Date(this.tenorStartDateValue);
    if (this.contractTypeValue === this.autoExecString && this.tenorMonthValue) {
      this.checkTenorMonthsValidity();
    }

    if (!this.tenorStartDateValue) {
      this.checkFieldValidity(this.tenorStartDateElement);
      this.isTenorDateValid = false;
      return;
    }
    if (this.contractTypeValue === this.autoExecString && !this.checkDate(startDate)) {
      displayErrorUtil(this.tenorStartDateElement, 'Must be less than 12 months forward');
      this.isTenorDateValid = false;
    } else {
      this.isTenorDateValid = true;
      clearErrorUtil(this.tenorStartDateElement);
    }
    return;
  }

  private checkDate(dateTime: Date, numMonths?: number): boolean {
    const currentDate = new Date();
    const futureDate = new Date();
    futureDate.setFullYear(
      dateTime.getFullYear(),
      dateTime.getMonth() + (numMonths || 0),
      dateTime.getDate(),
    );
    const maxDate = new Date();
    maxDate.setFullYear(
      currentDate.getFullYear() + 1,
      currentDate.getMonth(),
      currentDate.getDate(),
    );
    return futureDate.getTime() < maxDate.getTime();
  }

  private checkTenorValidity() {
    this.validateTenorStartDateValue();
    this.checkFormCompletionStatus();
  }

  private checkTenorMonthsValidity() {
    this.isTenorMonthsValid = true;
    clearErrorUtil(this.tenorMonthElement);
    if (!this.tenorMonthValue) {
      this.isTenorMonthsValid = false;
    }
    if (Number(this.tenorMonthValue) <= 0) {
      displayErrorUtil(
        this.tenorMonthElement,
        'Tenor Months value should be greater than zero (0)',
      );
      this.isTenorMonthsValid = false;
    }
    const startDate = new Date(this.tenorStartDateValue);
    if (
      this.contractTypeValue === this.autoExecString &&
      !this.checkDate(startDate, Number(this.tenorMonthValue))
    ) {
      displayErrorUtil(
        this.tenorMonthElement,
        'Tenor Start Date + months must be less than 12 months forward',
      );
      this.isTenorMonthsValid = false;
    }

    this.checkFormCompletionStatus();
  }

  private checkOfferedCapacityValidity() {
    this.isCapacityValid = true;
    clearErrorUtil(this.capacityElement);
    if (this.capacityValue) {
      this.capacityValue = roundNumber(this.capacityValue, 3, 10, 'UP')?.toString();
      if (Number(this.capacityValue) < 1 || Number(this.capacityValue) > 10) {
        this.isCapacityValid = false;
        displayErrorUtil(this.capacityElement, 'Must be between 1MW and 10MW');
      }
    } else if (!this.capacityValue) {
      displayErrorUtil(this.capacityElement);
    }

    this.checkFormCompletionStatus();
  }

  private checkAndSetNARfqEnums() {
    if (!this.recDeliveryMethodologyValue) {
      this.recDeliveryMethodologyValue = undefined;
    }
    if (!this.applicableTrackingSystemValue) {
      this.applicableTrackingSystemValue = undefined;
    }
    if (!this.applicableRECProgramValue) {
      this.applicableRECProgramValue = undefined;
    }
    if (!this.performanceGuaranteeMethodValue) {
      this.performanceGuaranteeMethodValue = undefined;
    }
    if (!this.negativePriceMethodValue) {
      this.negativePriceMethodValue = undefined;
    }
  }

  public filteredContractTypes(): Array<EnumTranslate> {
    const virtualPpaFilter: String[] = [
      CONTRACT_TYPE.MINI_POST_BUILD_VPPA_AUTO_EXECUTED,
      CONTRACT_TYPE.NEW_BUILD_VPPA,
      CONTRACT_TYPE.POST_BUILD_VPPA,
    ];
    const physicalPpaFilter: String[] = [CONTRACT_TYPE.NEW_BUILD_PPA, CONTRACT_TYPE.POST_BUILD_PPA];
    const recFilter: String[] = [CONTRACT_TYPE.REC];
    if (this.contractTypes) {
      switch (this.productTypeValue) {
        case PRODUCT_TYPE.VIRTUAL_PPA: {
          return this.contractTypes.filter(
            (x) =>
              virtualPpaFilter.includes(x.ENUM_VALUE) ||
              (x.ENUM_VALUE === CONTRACT_TYPE.MINI_POST_BUILD_VPPA_AUTO_EXECUTED &&
                this.companyService.canAutoExecute() &&
                this.userService.getFilteredPermissions().includes(PROFILE_RIGHTS.AUTO_EXECUTE)),
          );
        }

        case PRODUCT_TYPE.PHYSICAL_PPA: {
          return this.contractTypes.filter((x) => physicalPpaFilter.includes(x.ENUM_VALUE));
        }
        case PRODUCT_TYPE.RECS: {
          return this.contractTypes.filter((x) => recFilter.includes(x.ENUM_VALUE));
        }
        default: {
          return this.contractTypes;
        }
      }
    }
    return [];
  }

  public checkProductTypeValidity() {
    this.isContractTypeValid = true;
    this.cleanRecOnlyValues();
    if (this.productTypeValue) {
      setTimeout(() => {
        if (this.productTypeValue === PRODUCT_TYPE.RECS) {
          this.contractTypeValue = CONTRACT_TYPE.REC;
          this.isContractTypeValid = true;
          clearErrorUtil(this.contractTypeElement);
          this.loadRequiredFields();
          return;
        } else if (this.productTypeValue && this.contractTypeValue) {
          this.contractTypeValue = null;
          clearErrorUtil(this.productTypeElement);
          this.isContractTypeValid = false;
          this.validateContractType();
        }
        this.checkFormCompletionStatus();
      }, 10);
    }
  }
  public cleanRecOnlyValues() {
    if (this.productTypeValue !== PRODUCT_TYPE.RECS) {
      this.fixedPriceValue = null;
      this.floorQuantityValue = null;
      return;
    }
  }
}
